/**
 * Cognito reports a refusal from a Lambda trigger by bouncing the browser back
 * to us with `?error_description=`, wrapping whatever the trigger threw:
 *
 *   PreSignUp failed with error This account has two-factor authentication…
 *
 * The wrapper names an implementation detail nobody signing in has heard of, so
 * it is stripped and the sentence the trigger wrote is what gets shown. An
 * unrecognised shape is passed through unchanged rather than guessed at.
 */
const TRIGGER_PREFIX = /^\w+ failed with error\s*/i;

/** How long a description may be before we stop believing it is for a reader. */
const MAX_LENGTH = 300;

/**
 * The sentence to show for a failed federated sign-in, or null when there is
 * nothing worth showing.
 */
export function describeOauthError(
    description: string | null | undefined,
): string | null {
    if (!description) {
        return null;
    }

    const message = description.replace(TRIGGER_PREFIX, "").trim();

    // Cognito also sends machine-shaped descriptions for its own failures
    // (`invalid_request`, `access_denied`). Those say nothing to a reader.
    if (!message || !/\s/.test(message) || message.length > MAX_LENGTH) {
        return "We could not complete that sign-in. Please try again.";
    }

    // One full stop, whether the trigger ended with one, Cognito added its own,
    // or neither did
    return `${message.replace(/\.+$/, "")}.`;
}

/**
 * Take the OAuth result out of the address bar.
 *
 * The description is read once, on the load that carries it. Leaving it in the
 * url means a refresh -- or a bookmark -- replays a failure that already
 * happened, and re-renders the error over a screen the user has since moved on
 * from.
 */
export function stripOauthParamsFromUrl(): void {
    if (typeof window === "undefined") {
        return;
    }

    const url = new URL(window.location.href);
    const before = url.search;

    for (const param of ["error", "error_description", "code", "state"]) {
        url.searchParams.delete(param);
    }

    if (url.search !== before) {
        window.history.replaceState({}, "", url.toString());
    }
}
